import { stegaClean } from "next-sanity";
import type { PortableTextBlock } from "sanity";
import { resolveI18nText } from "@/sanity/globals/I18n";
import type { GROQImage, IAffiliateGlobalSections } from "@/sanity/lib/definitions";

/**
 * Reads the Rules cards from the global "Affiliate Global Sections" document
 * the page points at, or from the page itself when the toggle is off. Only the
 * cards are shared — the chip, the heading and the CTA stay on the page.
 *
 * The two affiliate designs draw this section differently, so what is shared is
 * the content, not the markup.
 *
 * Falls back to the page's own cards whenever the global would render nothing:
 * a toggle turned on before the reference is picked, or picked before the cards
 * are filled, must not blank the section on a live partner page.
 */

export interface IAffiliateRulesSource {
  useGlobalRules?: boolean;
  affiliateGlobalSectionsRef?: IAffiliateGlobalSections;
  rulesLanguage?: string;
  rulesChip?: string;
  rulesText?: PortableTextBlock[];
  rulesItems?: Array<{ icon?: GROQImage; text?: string }>;
  settings?: { language?: string };
}

export interface IResolvedAffiliateRules {
  chip?: string;
  text?: PortableTextBlock[];
  items: Array<{ icon?: GROQImage; text?: string }>;
}

export function resolveAffiliateRules(data?: IAffiliateRulesSource): IResolvedAffiliateRules {
  const chip = data?.rulesChip;
  const text = data?.rulesText;
  const localItems = data?.rulesItems ?? [];

  if (!data?.useGlobalRules) return { chip, text, items: localItems };

  const globalItems = data?.affiliateGlobalSectionsRef?.rulesItems ?? [];
  if (!globalItems.length) return { chip, text, items: localItems };

  // stegaClean: in draft mode the CMS language carries invisible characters and
  // would miss the translation key (same guard as AffiliateHeroItems).
  const language = stegaClean(data?.rulesLanguage) || stegaClean(data?.settings?.language);

  // Filter on the text, not the icon: a document filled only in German would
  // otherwise pass on an English page through its icons alone and replace that
  // page's working cards with blank ones.
  const items = globalItems
    .map((item) => ({ icon: item?.icon, text: resolveI18nText(item?.text, item?.textI18n, language) }))
    .filter((item) => stegaClean(item.text));

  if (!items.length) return { chip, text, items: localItems };

  return { chip, text, items };
}
